import { useEffect, useState } from "react";
import {
  useDiseases,
  useCreateDiseaseHistory,
  useDeleteDiseaseHistory,
  useResolveCustomDisease,
  useConfirmDiseaseHistory,
} from "@/hooks/useConsultation";
import { useToast } from "@/hooks/use-toast";
import {
  SearchableChipDialog,
  type ChipGroup,
} from "@/components/consultation/shared/SearchableChipDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
  PatientDiseaseHistory,
  CreateDiseaseHistoryRequest,
  DiseaseStatus,
} from "@/types/appointments";

interface DiseaseUIState {
  inactiveSelfIds?: Set<string>;
  inactiveConfirmedIds?: Set<string>;
}

interface AddDiseaseHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  appointmentId: string;
  confirmedHistory?: PatientDiseaseHistory[];
  selfReportedHistory?: PatientDiseaseHistory[];
  defaultSource?: "intake_form" | "prescription" | "meal_plan";
  lockSource?: boolean;
  allowProvisionalStatus?: boolean;
  uiState?: DiseaseUIState;
  customDiseaseToMap?: PatientDiseaseHistory | null;
}

export const AddDiseaseHistoryDialog = ({
  open,
  onOpenChange,
  patientId,
  appointmentId,
  confirmedHistory = [],
  selfReportedHistory = [],
  defaultSource = "intake_form",
  lockSource: _lockSource = false,
  allowProvisionalStatus = false,
  uiState,
  customDiseaseToMap = null,
}: AddDiseaseHistoryDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDiseaseStatus, setSelectedDiseaseStatus] =
    useState<DiseaseStatus>("confirmed");
  const [pendingMapDiseaseId, setPendingMapDiseaseId] = useState<string | null>(
    null
  );
  const { toast } = useToast();

  const { data: diseases = [] } = useDiseases();
  const createMutation = useCreateDiseaseHistory();
  const deleteMutation = useDeleteDiseaseHistory();
  const resolveCustomMutation = useResolveCustomDisease(
    appointmentId,
    patientId
  );
  const confirmMutation = useConfirmDiseaseHistory(appointmentId, patientId);

  const isMappingCustomDisease = customDiseaseToMap != null;
  const canSelectProvisional =
    allowProvisionalStatus &&
    defaultSource === "prescription" &&
    !isMappingCustomDisease;
  const isAppointmentDiagnosisSource =
    defaultSource === "prescription" || defaultSource === "meal_plan";
  const dialogTitle = isAppointmentDiagnosisSource
    ? "Add Diagnosis"
    : "Add Pre-Existing Conditions";
  const searchLabel = isAppointmentDiagnosisSource
    ? "Add diagnosis here"
    : "Add health history here";

  useEffect(() => {
    if (open) setSelectedDiseaseStatus("confirmed");
  }, [open]);

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (
      error &&
      typeof error === "object" &&
      "message" in error &&
      typeof error.message === "string"
    ) {
      return error.message;
    }
    return fallback;
  };

  const handleAddClick = async (diseaseId: string) => {
    if (isMappingCustomDisease) {
      setPendingMapDiseaseId(diseaseId);
      return;
    }

    try {
      const requestData: CreateDiseaseHistoryRequest = {
        diagnosed_at: new Date().toISOString(),
        disease_status:
          defaultSource === "intake_form"
            ? "self_reported"
            : canSelectProvisional
              ? selectedDiseaseStatus
              : "confirmed",
        source: defaultSource,
        disease: diseaseId,
      };

      await createMutation.mutateAsync({
        id: defaultSource === "intake_form" ? patientId : appointmentId,
        data: requestData,
      });
      toast({
        title: "Success",
        description: "Disease history added successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(
          error,
          isMappingCustomDisease
            ? "Failed to map custom disease"
            : "Failed to add disease history"
        ),
        variant: "destructive",
      });
    }
  };

  const pendingMapDisease = diseases.find((d) => d.id === pendingMapDiseaseId);

  const handleConfirmMap = async () => {
    if (!isMappingCustomDisease || !pendingMapDiseaseId) return;

    try {
      await resolveCustomMutation.mutateAsync({
        draftItemId: customDiseaseToMap.id,
        data: { match_disease_id: pendingMapDiseaseId },
      });
      toast({
        title: "Success",
        description: "Custom disease mapped for review",
      });
      setPendingMapDiseaseId(null);
      onOpenChange(false);
    } catch (error) {
      setPendingMapDiseaseId(null);
      onOpenChange(false);
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to map custom disease"),
        variant: "destructive",
      });
    }
  };

  const confirmedHistoryFiltered = confirmedHistory.filter(
    (h) => h.disease != null
  );
  const selfReportedHistoryFiltered = selfReportedHistory.filter(
    (h) => h.disease != null
  );
  const allHistory = [
    ...confirmedHistoryFiltered,
    ...selfReportedHistoryFiltered,
  ];

  const handleConfirmClick = async (id: string) => {
    try {
      await confirmMutation.mutateAsync(id);
      toast({
        title: "Confirmed",
        description: "Moved to Past Medical History",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error, "Failed to confirm condition"),
        variant: "destructive",
      });
    }
  };

  const handleRemoveClick = async (id: string) => {
    const entry = allHistory.find((h) => h.id === id);
    if (!entry) return;
    try {
      await deleteMutation.mutateAsync({
        id: defaultSource === "intake_form" ? patientId : appointmentId,
        rowId: entry.id,
        source: defaultSource,
      });
      toast({
        title: "Success",
        description: "Disease history removed successfully",
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to remove disease history",
        variant: "destructive",
      });
    }
  };

  // Filter available diseases (exclude already-added ones)
  const addedDiseaseIds = new Set(
    allHistory
      .map((h) => h.disease?.id)
      .filter((id): id is string => Boolean(id))
  );
  const availableDiseases = diseases.filter((disease) => {
    const matchesSearch =
      disease.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      disease.long_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      disease.short_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      disease.other_names?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      disease.icd10_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const notAdded = isMappingCustomDisease || !addedDiseaseIds.has(disease.id);
    return matchesSearch && notAdded;
  });

  const chipClass = "border-border/80 bg-card text-foreground";
  const removeClass =
    "bg-muted text-muted-foreground hover:bg-destructive/20 hover:text-destructive";

  const toChipItem = (h: PatientDiseaseHistory) => ({
    id: h.id,
    label: h.disease!.display_name,
  });

  const statusControl = canSelectProvisional ? (
    <div className="space-y-2">
      <p className="z-secondary text-muted-foreground">Diagnosis status</p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        <Button
          type="button"
          variant={
            selectedDiseaseStatus === "confirmed" ? "default" : "outline"
          }
          size="sm"
          onClick={() => setSelectedDiseaseStatus("confirmed")}
          className={cn(
            "whitespace-nowrap",
            selectedDiseaseStatus === "confirmed"
              ? "bg-primary text-primary-foreground"
              : "text-foreground"
          )}
        >
          Confirmed
        </Button>
        <Button
          type="button"
          variant={
            selectedDiseaseStatus === "provisional" ? "default" : "outline"
          }
          size="sm"
          onClick={() => setSelectedDiseaseStatus("provisional")}
          className={cn(
            "whitespace-nowrap",
            selectedDiseaseStatus === "provisional"
              ? "bg-primary text-primary-foreground"
              : "text-foreground"
          )}
        >
          Provisional
        </Button>
      </div>
    </div>
  ) : null;

  const addedGroups: ChipGroup[] | undefined = isMappingCustomDisease
    ? undefined
    : [
        {
          key: "confirmed",
          label: "Past Medical History",
          chipClassName: chipClass,
          removeClassName: removeClass,
          items: confirmedHistoryFiltered
            .filter((h) => !uiState?.inactiveConfirmedIds?.has(h.id))
            .map(toChipItem),
          inactiveItems: confirmedHistoryFiltered
            .filter((h) => uiState?.inactiveConfirmedIds?.has(h.id))
            .map(toChipItem),
        },
        {
          key: "self_reported",
          label: "Patient-Reported",
          chipClassName: chipClass,
          removeClassName: removeClass,
          onConfirmItem: handleConfirmClick,
          items: selfReportedHistoryFiltered
            .filter((h) => !uiState?.inactiveSelfIds?.has(h.id))
            .map(toChipItem),
          inactiveItems: selfReportedHistoryFiltered
            .filter((h) => uiState?.inactiveSelfIds?.has(h.id))
            .map(toChipItem),
        },
      ];

  return (
    <>
      <SearchableChipDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingMapDiseaseId(null);
          onOpenChange(nextOpen);
        }}
        title={isMappingCustomDisease ? "Map Custom Disease" : dialogTitle}
        addedGroups={addedGroups}
        onRemoveItem={handleRemoveClick}
        controlsSlot={statusControl}
        searchLabel={
          isMappingCustomDisease
            ? `Map "${customDiseaseToMap.custom_disease_name ?? "Custom disease"}" to an existing disease`
            : searchLabel
        }
        searchPlaceholder="Search disease"
        searchResults={availableDiseases.map((d) => ({
          id: d.id,
          label: d.display_name,
        }))}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        minSearchChars={2}
        emptySearchMessage={
          isMappingCustomDisease
            ? "Disease not found"
            : "No matching diseases found"
        }
        onAddItem={handleAddClick}
        showCustomAdd={isMappingCustomDisease}
        customAddDisabled
        customAddLabel="Add new disease"
      />

      <Dialog
        open={pendingMapDisease != null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingMapDiseaseId(null);
        }}
      >
        <DialogContent className="sm:max-w-[440px] border-border/60">
          <DialogHeader>
            <DialogTitle>Confirm mapping</DialogTitle>
            <DialogDescription>
              Map "{customDiseaseToMap?.custom_disease_name ?? "Custom disease"}
              " to "{pendingMapDisease?.display_name}". Filed as
              patient-reported, pending clinical confirmation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              onClick={handleConfirmMap}
              disabled={resolveCustomMutation.isPending}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
